// Skill Taxonomy Constants
// Mirrors the Ruby SkillTaxonomy module for frontend use

// Skill kinds to Japanese labels mapping
export const KIND_LABELS: Record<string, string> = {
  'language': '言語',
  'os': 'OS',
  'cloud': 'クラウド',
  'db': 'データベース',
  'runtime': 'ランタイム',
  'framework': 'フレームワーク',
  'meta_framework': 'メタフレームワーク',
  'library': 'ライブラリ',
  'middleware': 'ミドルウェア',
  'iac': 'IaC',
  'ci_cd': 'CI/CD',
  'monitoring': '監視',
  'vcs': 'バージョン管理',
  'virtualization': '仮想化',
  'container_orchestrator': 'コンテナオーケストレーション',
  'analytics_bi': '分析・BI',
  'search_engine': '検索エンジン',
  'cloud_service': 'クラウドサービス',
  'design_tool': 'デザインツール',
  'game_engine': 'ゲームエンジン',
  'rpa': 'RPA',
  'crm_erp': 'CRM/ERP',
  'ecommerce_cms': 'EC/CMS',
  'office_suite': 'オフィススイート',
  'practice': '実践・概念',
  'ai_ml_platform': 'AI/ML',
  'generic': 'その他'
};

// Skill kinds to display order mapping (lower number = higher priority)
export const SIGNATURE_RANKS: Record<string, number> = {
  'language': 100,
  'os': 150,
  'cloud': 160,
  'db': 170,
  'runtime': 180,
  'framework': 200,
  'meta_framework': 210,
  'cloud_service': 220,
  'middleware': 260,
  'search_engine': 350,
  'ecommerce_cms': 365,
  'ci_cd': 420,
  'iac': 430,
  'container_orchestrator': 432,
  'monitoring': 440,
  'vcs': 450,
  'virtualization': 455,
  'analytics_bi': 500,
  'ai_ml_platform': 520,
  'design_tool': 600,
  'game_engine': 610,
  'crm_erp': 470,
  'office_suite': 470,
  'practice': 700,
  'library': 300,
  'generic': 1000
};

/**
 * Get Font Awesome icon class for skill kind
 * @param kind - The skill kind string
 * @returns Font Awesome icon class string
 */
export const skillKindIcon = (kind: string): string => {
  switch (kind) {
    case 'language':
      return 'fas fa-code';
    case 'os':
      return 'fas fa-desktop';
    case 'cloud':
      return 'fas fa-cloud';
    case 'db':
      return 'fas fa-database';
    case 'runtime':
      return 'fas fa-play-circle';
    case 'framework':
      return 'fas fa-layer-group';
    case 'meta_framework':
      return 'fas fa-cubes';
    case 'library':
      return 'fas fa-book';
    case 'middleware':
      return 'fas fa-cogs';
    case 'iac':
      return 'fas fa-infinity';
    case 'ci_cd':
      return 'fas fa-sync-alt';
    case 'monitoring':
      return 'fas fa-chart-line';
    case 'vcs':
      return 'fas fa-code-branch';
    case 'virtualization':
      return 'fas fa-server';
    case 'container_orchestrator':
      return 'fas fa-ship';
    case 'analytics_bi':
      return 'fas fa-chart-bar';
    case 'search_engine':
      return 'fas fa-search';
    case 'cloud_service':
      return 'fas fa-cloud-upload-alt';
    case 'design_tool':
      return 'fas fa-paint-brush';
    case 'game_engine':
      return 'fas fa-gamepad';
    case 'rpa':
      return 'fas fa-robot';
    case 'crm_erp':
      return 'fas fa-building';
    case 'ecommerce_cms':
      return 'fas fa-shopping-cart';
    case 'office_suite':
      return 'fas fa-file-alt';
    case 'practice':
      return 'fas fa-lightbulb';
    case 'ai_ml_platform':
      return 'fas fa-brain';
    default: // 'generic'
      return 'fas fa-tools';
  }
};

/**
 * Group skills by kind and sort by SIGNATURE_RANKS order
 * @param skills - Array of skill objects with id, name, and kind
 * @returns Object with kind as key and skills array as value, sorted by SIGNATURE_RANKS
 */
export const groupSkillsByKind = (skills: Array<{ id: number; name: string; kind: string }>): Record<string, Array<{ id: number; name: string; kind: string }>> => {
  if (!skills || skills.length === 0) {
    return {};
  }

  // Group skills by kind
  const grouped = skills.reduce((acc, skill) => {
    const kind = skill.kind || 'generic';
    if (!acc[kind]) {
      acc[kind] = [];
    }
    acc[kind].push(skill);
    return acc;
  }, {} as Record<string, Array<{ id: number; name: string; kind: string }>>);

  // Sort kinds by SIGNATURE_RANKS order (lower number = higher priority)
  const sortedKinds = Object.keys(SIGNATURE_RANKS).sort((a, b) => SIGNATURE_RANKS[a] - SIGNATURE_RANKS[b]);

  // Return only kinds that have skills, in the correct order
  const result: Record<string, Array<{ id: number; name: string; kind: string }>> = {};
  sortedKinds.forEach(kind => {
    if (grouped[kind] && grouped[kind].length > 0) {
      result[kind] = grouped[kind];
    }
  });

  return result;
};
